import { useCallback, useEffect, useState, useMemo } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Components
import Select from '../../../../components/atoms/select';
import Button from '../../../../components/atoms/button';
import Table, { TableColumn } from '../../../../components/organisms/table';
import Text from '../../../../components/atoms/text';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import { NotificationToast } from '../../../../components/organisms/notification-toast';

// Services
import YardService, { IYard } from '../../../../services/Yard.service';

// Utils & Types
import { EditableChangeEvent } from '../../../../utils/types/editable';
import { dateObjToStr } from '../../../../utils/date';
import { HomeLoaderData } from '../../../../types/homeLoaderData';
import InputCalendar from '../../../../components/molecules/input-calendar';
import MaintenanceService, {
	IMaintenance,
	MaintenanceR,
} from '../../../../services/Maintenance.service';

// ================ Constants & Types ================
interface MaintenanceForm {
	yard: Partial<IYard>;
	dateRetroactive?: Date;
}

const DEFAULT_FORM: MaintenanceForm = {
	yard: {},
	dateRetroactive: undefined,
};

const DEFAULT_PAGINATION: PaginationProps = {
	pageCurrent: 1,
	pageSize: 10,
	totalItems: 0,
	totalPages: 1,
};

// ================ Main Component ================
const AuditoryMaintenance = () => {
	const { t } = useTranslation();
	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;

	// ================ State ================
	const [yards, setYards] = useState<IYard[]>([]);
	const [form, setForm] = useState<MaintenanceForm>(DEFAULT_FORM);
	const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
	const [maintenances, setMaintenances] = useState<MaintenanceR[]>([]);
	const [pagination, setPagination] =
		useState<PaginationProps>(DEFAULT_PAGINATION);

	// ================ Table Columns ================
	const columns: TableColumn<MaintenanceR>[] = useMemo(
		() => [
			{
				key: 'actions',
				label: t('misc.remove'),
				children: (row, index) => (
					<input
						type="checkbox"
						checked={!!row.checked}
						onChange={() => handleCheck(index)}
					/>
				),
			},
			{ key: 'container', label: t('port_management.container') },
			{
				key: 'date',
				label: t('date.title'),
				children: (row) => (
					<Text className="text-xs">
						{dateObjToStr(row.date, true)}
					</Text>
				),
			},
			{
				key: 'size',
				label: t('misc.size'),
			},
			{
				key: 'voltage',
				label: t('maintenance.voltage'),
			},
			{
				key: 'hh',
				label: t('maintenance.hh'),
			},
			{
				key: 'manufactory',
				label: t('maintenance.manufactory'),
			},
		],
		[t, maintenances]
	);

	const fetchYards = useCallback(async () => {
		if (!accountId) return;

		try {
			const response = await YardService.getAll({
				isActive: 1,
				idClient: Number(accountId),
			});

			if (response?.data) {
				setYards(response.data);
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_yards'));
		}
	}, [accountId, t]);

	const fetchMonitoring = useCallback(async () => {
		if (!form.yard.id) return;
		try {
			const response = await MaintenanceService.getAllAuditory(
				{
					pageCurrent: pagination.pageCurrent,
					pageSize: pagination.pageSize,
					idClient: Number(accountId),
				},
				form.yard?.id,
				form.dateRetroactive
			);

			if (response) {
				setMaintenances(response.data);
				setPagination((prev) => ({
					...prev,
					totalItems: response.totalItems,
					totalPages: response.totalPages,
				}));
			} else {
				setMaintenances([]);
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_movements'));
		}
	}, [pagination.pageCurrent, pagination.pageSize, searchTrigger]);

	// ================ Effects ================
	useEffect(() => {
		fetchYards();
	}, [fetchYards]);

	useEffect(() => {
		fetchMonitoring();
	}, [fetchMonitoring]);

	// ================ Handlers ================
	const handleCheck = (index?: number) => {
		setMaintenances((prev) =>
			prev.map((maintenance, maintenanceIndex) =>
				maintenanceIndex === index
					? { ...maintenance, checked: !maintenance.checked }
					: maintenance
			)
		);
	};

	const handleYardChange = useCallback(
		(e: EditableChangeEvent) => {
			const yard = yards.find((y) => y.name === e.target.value);
			if (!yard) return;

			setForm((prev) => ({ ...prev, yard }));
		},
		[yards]
	);

	const handleSearch = useCallback(() => {
		if (!form.yard.id) {
			NotificationToast.Warn(t('monitor.select_yard'));
			return;
		}
		setSearchTrigger((prev) => !prev);
	}, [form.yard.id, t]);

	const handleResetFilters = useCallback(() => {
		// Reset form to default values
		setForm(DEFAULT_FORM);
		// Trigger search to show all results
		setSearchTrigger((prev) => !prev);
		// Reset to first page
		setPagination((prev) => ({ ...prev, pageCurrent: 1 }));
	}, []);

	const handlePaginationUpdate = useCallback(
		(pageSize: number, pageCurrent: number) => {
			setPagination((prev) => ({ ...prev, pageSize, pageCurrent }));
		},
		[]
	);

	const handleSave = async () => {
		if (!form.yard.id) {
			NotificationToast.Warn(t('monitor.select_yard'));
			return;
		}
		const ids = [];
		for (let container of maintenances) {
			if (container.checked) ids.push(container.id);
		}
		if (ids.length == 0) {
			NotificationToast.Warn(
				t('port_management.monitoring.not_selected')
			);
			return;
		}
		try {
			await MaintenanceService.delete(ids);
			NotificationToast.Success(t('alert.success.generic'));
			handleSearch();
		} catch (error) {
			NotificationToast.Warn(t('alert.error.generic'));
		}
	};

	const handleDateChange = (date: Date) => {
		setForm((prev) => ({ ...prev, dateRetroactive: date }));
	};

	// ================ Render Helpers ================
	const renderFilters = () => (
		<div className="flex gap-x-8 gap-y-4 flex-wrap items-end">
			<Select
				boxClassName="min-w-60"
				name="yard"
				value={form.yard.name || ''}
				onChange={handleYardChange}
				label={t('port_management.yard')}
				placeholder={t('port_management.yard')}
				options={yards.map((y) => y.name)}
				disabled={!accountId}
				required
			/>

			<InputCalendar
				className="min-w-60"
				name="date"
				onDateChange={handleDateChange}
				label={t('date.retroactive')}
				placeholder={t('date.retroactive')}
			/>

			<div className="flex gap-2">
				<Button onClick={handleSearch}>{t('misc.search')}</Button>

				<Button theme="light" onClick={handleResetFilters}>
					{t('misc.reset')}
				</Button>
			</div>
		</div>
	);

	const renderTable = () => (
		<div className="overflow-x-auto w-full flex flex-col gap-8">
			<Table
				data={maintenances}
				columns={columns}
				className={classNames(
					'grid-cols-[auto_1fr_1fr_1fr_1fr_1fr_1fr]'
				)}
			/>
		</div>
	);

	// ================ Render ================
	return (
		<div className="flex flex-col gap-4 px-8 py-4">
			{renderFilters()}
			{renderTable()}
			<Button className="w-fit self-end" onClick={handleSave}>
				{t('misc.save')}
			</Button>
			<Pagination {...pagination} onUpdate={handlePaginationUpdate} />
		</div>
	);
};

export default AuditoryMaintenance;
